import { parseMoneyToCents, tt, type MoneyInput } from '@agos/shared'

/**
 * What should be in the drawer, and whether what is being taken out fits.
 *
 * The backend counts from the last Z-report, because that is when the register's own
 * counters were last zeroed — cash taken since then, plus what was put in, minus
 * what was taken out.
 *
 * FB-074 lives here. Legacy renders that balance directly above the withdrawal
 * button and never compares the two, so a withdrawal for more than the drawer holds
 * goes to the register and leaves the balance negative. Nothing downstream checks it
 * either: `FiskalClosureService` rejects only a *zero* amount.
 */

export interface DrawerStatus {
  kasaId?: string
  /** The last Z-report — the boundary the figures are counted from. */
  sinceDate?: string | null
  cashFromReceipts?: MoneyInput
  cardTotal?: MoneyInput
  cardBreakdown?: Record<string, MoneyInput>
  deposits?: MoneyInput
  withdrawals?: MoneyInput
  drawerBalance?: MoneyInput
}

export interface DrawerReading {
  cashFromReceiptsCents: number
  cardTotalCents: number
  depositsCents: number
  withdrawalsCents: number
  balanceCents: number
  /** Card takings by method, in the register's own labels. */
  cardLines: { label: string; cents: number }[]
  sinceDate: string | null
  /** The balance the components add up to, which need not be the stored one. */
  derivedBalanceCents: number
  /** The stored balance disagrees with its own parts. */
  inconsistent: boolean
}

/** Anything under a cent is rounding; a cent is a discrepancy. */
const TOLERANCE_CENTS = 0

export function readDrawer(status: DrawerStatus | undefined): DrawerReading | null {
  if (!status) return null

  const cashFromReceiptsCents = parseMoneyToCents(status.cashFromReceipts)
  const depositsCents = parseMoneyToCents(status.deposits)
  const withdrawalsCents = parseMoneyToCents(status.withdrawals)
  const balanceCents = parseMoneyToCents(status.drawerBalance)
  const derivedBalanceCents = cashFromReceiptsCents + depositsCents - withdrawalsCents

  return {
    cashFromReceiptsCents,
    cardTotalCents: parseMoneyToCents(status.cardTotal),
    depositsCents,
    withdrawalsCents,
    balanceCents,
    cardLines: Object.entries(status.cardBreakdown ?? {}).map(([label, amount]) => ({
      label,
      cents: parseMoneyToCents(amount),
    })),
    sinceDate: status.sinceDate ?? null,
    derivedBalanceCents,
    inconsistent: Math.abs(derivedBalanceCents - balanceCents) > TOLERANCE_CENTS,
  }
}

/**
 * What period the drawer figures cover - §9.410.
 *
 * **The owner read an all-time card total as today's takings**, and they were entitled to: the panel
 * printed the boundary in 11px grey *and guarded it on `sinceDate` being present*, so a register that
 * has never been closed showed no period at all. `takingsSince` has no lower bound when there is no
 * `result = 'ok'` z-report row - `(CAST(:since AS timestamptz) IS NULL OR …)` - which is a defensible
 * default for a drawer and indefensible in silence.
 *
 * **A register can legitimately have no recorded closure even though it was closed.** §10A.157 lost
 * the row to a `jsonb` binding and §10A.182 found that the `REQUIRES_NEW` meant to contain that
 * failure had never been entered. Both are fixed; both were fixed *after* the closures that are
 * missing.
 *
 * - `unbounded` - nothing is recorded, so the figures are from the beginning.
 * - `stale` - the last closure is on an earlier day, and `days` says how many it spans.
 * - `today` - the ordinary case, and nothing needs saying beyond the timestamp.
 */
export type DrawerWindow =
  | { kind: 'unbounded' }
  | { kind: 'stale'; days: number }
  | { kind: 'today' }

/**
 * **Whole days in local time, not elapsed hours.** A Z at 23:30 last night and a reading at 00:30 is
 * *yesterday's closure*, which is what an operator means and what `startOfDay` differences give;
 * counting hours would call it the same period.
 */
export function drawerWindow(sinceDate: string | null, now: Date = new Date()): DrawerWindow {
  if (sinceDate === null || sinceDate.trim() === '') return { kind: 'unbounded' }

  const since = new Date(sinceDate)
  if (Number.isNaN(since.getTime())) return { kind: 'unbounded' }

  const midnight = (moment: Date) =>
    new Date(moment.getFullYear(), moment.getMonth(), moment.getDate()).getTime()
  const days = Math.round((midnight(now) - midnight(since)) / 86_400_000)

  // A boundary in the future is a clock disagreement between this browser and the server, not a
  // period to describe. Reading it as *today* is the harmless direction: the timestamp is still
  // printed underneath.
  if (days <= 0) return { kind: 'today' }
  return { kind: 'stale', days: days + 1 }
}

/**
 * What to say about it, or empty on the ordinary case.
 *
 * **`days + 1` above, and the wording is why**: a closure yesterday means the figures cover yesterday
 * *and* today - two days, not one. Saying *"za 1 deň"* about a two-day total would be a new wrong
 * number in place of the old one.
 */
export function drawerWindowNote(window: DrawerWindow): string {
  if (window.kind === 'unbounded') {
    return tt(
      'Na tejto kase nie je zaznamenaná žiadna Z-uzávierka, takže tieto čísla sú za celé obdobie - nie za dnes.',
    )
  }
  if (window.kind === 'stale') {
    return `${tt('Tieto čísla sú za')} ${window.days} ${tt('dni - od poslednej Z-uzávierky, nie za dnes.')}`
  }
  return ''
}

export type AmountProblem =
  | { kind: 'empty' }
  | { kind: 'not-a-number' }
  | { kind: 'not-positive' }
  /** A withdrawal larger than the drawer holds — FB-074. */
  | { kind: 'over-drawer'; balanceCents: number; shortByCents: number }

/**
 * Whether an amount can be sent.
 *
 * `over-drawer` is deliberately **not** fatal. The drawer figure counts receipts, so
 * it can legitimately lag reality — a sale rung up seconds ago, a payment that has
 * not registered. Refusing outright would stop a real cash-out; saying nothing is
 * how the drawer goes negative. So it is a warning the operator confirms.
 */
export function checkAmount(
  raw: string,
  action: 'deposit' | 'withdraw',
  drawer: DrawerReading | null,
): AmountProblem | null {
  const trimmed = raw.trim()
  if (trimmed === '') return { kind: 'empty' }

  const amount = Number(trimmed.replace(',', '.'))
  if (!Number.isFinite(amount)) return { kind: 'not-a-number' }
  if (amount <= 0) return { kind: 'not-positive' }

  if (action === 'withdraw' && drawer) {
    const cents = parseMoneyToCents(trimmed)
    if (cents > drawer.balanceCents) {
      return {
        kind: 'over-drawer',
        balanceCents: drawer.balanceCents,
        shortByCents: cents - drawer.balanceCents,
      }
    }
  }

  return null
}

/** Whether the button may be pressed at all. An over-drawer amount may. */
export function canSubmit(problem: AmountProblem | null): boolean {
  return problem === null || problem.kind === 'over-drawer'
}

/** Whether pressing it needs a second look first. */
export function needsSecondLook(problem: AmountProblem | null): boolean {
  return problem?.kind === 'over-drawer'
}

/** The amount in the shape `POST /fiskal/closure` wants — euros, not cents. */
export function amountForRequest(raw: string): number {
  return parseMoneyToCents(raw) / 100
}
